import { copyToClipboard } from '@mi-front/core';
import CheckIcon from '@mui/icons-material/Check';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { IconButton, IconButtonProps } from '@mui/material';
import { useEffect, useState } from 'react';

export interface CopyIconButtonProps {
  /** Text written to the clipboard when the button is clicked. */
  copyValue: string;
  onCopied?: () => void;
  size?: IconButtonProps['size'];
  disabled?: boolean;
  sx?: IconButtonProps['sx'];
  /** Font size applied to both the copy and check icons. */
  iconFontSize?: number;
}

/**
 * Compact icon button that copies a value to the clipboard and briefly shows a checkmark.
 */
export function CopyIconButton({
  copyValue,
  onCopied,
  size = 'small',
  disabled = false,
  sx,
  iconFontSize = 14,
}: CopyIconButtonProps) {
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!copied) {
      return;
    }

    const timeoutId = window.setTimeout(() => {
      setCopied(false);
    }, 2000);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [copied]);

  const handleClick = () => {
    copyToClipboard(copyValue);
    setCopied(true);
    onCopied?.();
  };

  return (
    <IconButton size={size} onClick={handleClick} disabled={disabled || copied} sx={sx}>
      {copied ? (
        <CheckIcon sx={{ fontSize: iconFontSize }} />
      ) : (
        <ContentCopyIcon sx={{ fontSize: iconFontSize }} />
      )}
    </IconButton>
  );
}
